import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Signal, effect, output } from '@angular/core';
import { SpecialDayOpeningHours, RegularOpeningHours, HolidayDatesOpeningHours, OpeningPeriods, BusinessConfiguration, HolidayOpeningHours } from '../../../../../../state-management/model/interfaces/state.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormArray, FormBuilder, FormGroup, Validators, FormsModule, FormControl, AbstractControl, ValidationErrors, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { WebsiteStateService } from '../../../../../../state-management/services/website-state.service';
import { periodsValidatorTest } from '../opening-hours-display-validator-function';
import { debounceTime, Subscription } from 'rxjs';
import { isHoliday } from 'feiertagejs';


@Component({
    selector: 'app-website-config-opening-hours-display',
    standalone: true,
    imports: [
        MatFormFieldModule,
        FormsModule,
        MatIcon,
        MatButtonModule,
        ReactiveFormsModule,
        MatInputModule,
        MatTooltipModule,
        MatCheckboxModule,
        CommonModule
    ],
    templateUrl: './website-config-opening-hours-display.component.html',
    styleUrl: './website-config-opening-hours-display.component.scss'
})
export class WebsiteConfigOpeningHoursDisplayComponent implements OnInit, OnDestroy {
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input({ required: true }) day!: SpecialDayOpeningHours | RegularOpeningHours | HolidayDatesOpeningHours | HolidayOpeningHours;
  readonly onOpeningPeriodsChanged = output<OpeningPeriods[]>();
  private sub: Subscription = new Subscription();

  public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public form!: FormGroup;

  constructor(private fb: FormBuilder, private websiteState: WebsiteStateService, private cdr: ChangeDetectorRef) {
    effect(() => {
        this.updatePeriods();
    });
   }

  ngOnInit(): void {
    this.form = this.fb.group({
      periods: this.fb.array(
        this.getPeriods().map(period =>
          this.fb.group({
            open: [period.open],
            close: [period.close],
          })
        ), [periodsValidatorTest()]
      ),
    });

    if (this.isHolidayDate(this.day)) {
      this.form.addControl('syncWithDefaultPeriods', this.fb.control(false));
    }

    this.sub = this.form.valueChanges.pipe(debounceTime(500)).subscribe((formData) => {
      if (this.form.valid) {
        this.saveOpeningPeriodsChanges(formData);
      } else {
        console.log('PERIODS CHANGES: Formular ungültig, keine Daten gespeichert.');
      }
    });
  }

  ngOnDestroy(): void {
    if(this.sub) this.sub.unsubscribe();
  }

  private getSyncWithDefaultPeriods(): boolean | undefined {
    return this.isHolidayDate(this.day) ? this.day.syncWithDefaultPeriods : undefined;
  }

  public isHolidayDate(day: SpecialDayOpeningHours | RegularOpeningHours | HolidayDatesOpeningHours | HolidayOpeningHours): day is HolidayDatesOpeningHours  {
    return (day as HolidayDatesOpeningHours)?.syncWithDefaultPeriods !== undefined;
  }

  private getPeriods(): OpeningPeriods[] {
    return 'periods' in this.day ? this.day.periods : this.day.defaultPeriods;
  }

  get periodsArray(): FormArray {
    return this.form.get('periods') as FormArray; 
  }

  public removePeriod(index: number) {
    const periodsArray = this.periodsArray;
    if (periodsArray) {
      periodsArray.removeAt(index);
      this.form.setControl('periods', periodsArray);
    }
  }

  public addPeriod(): void {
    const newPeriod = this.fb.group({
      open: [''], 
      close: [''], 
    });
    this.periodsArray.push(newPeriod);
  }

  public updatePeriods() {
    if(this.getSyncWithDefaultPeriods()) {
      if(this.metaData().openingHours.holidayOpeningHours.defaultPeriods != this.getPeriods()){
        this.periodsArray.clear();
        this.metaData().openingHours.holidayOpeningHours.defaultPeriods.forEach(period => {
          this.periodsArray.push(
            this.fb.group({
              open: [period.open],
              close: [period.close],
            })
          );
        });
      }   
    }
  }

  private saveOpeningPeriodsChanges(formData: { periods: OpeningPeriods[] } ): void {
    this.onOpeningPeriodsChanged.emit(formData.periods);
  }

}
